import { BehaviorSubject, Observable, of } from 'rxjs';
import * as RxOp from 'rxjs-operators';
import { COOKIE_DOMAIN } from 'settings';
import { jwtDecode } from 'jwt-decode';

import storageService, { StorageService } from './storage';

export interface ITokens {
  jwt_refresh_token: string;
  jwt_token: string;
}

export class TokenService {
  private tokens$: BehaviorSubject<ITokens>;

  constructor(private storageService: StorageService) {
    this.tokens$ = new BehaviorSubject(null);
  }

  public getAccessToken(): Observable<string> {
    return this.tokens$.pipe(
      RxOp.map(tokens => tokens?.jwt_token),
      RxOp.distinctUntilChanged(),
    );
  }

  public getTokens(): Observable<ITokens> {
    return this.tokens$.pipe(RxOp.distinctUntilChanged());
  }

  public setTokens(tokens: ITokens | null): Observable<ITokens> {
    this.tokens$.next(tokens);

    if (!tokens) {
      document.cookie = `authToken= ;domain=${COOKIE_DOMAIN}; path=/;expires= Thu, 01 Jan 1970 00:00:00 GMT`;
      return of(tokens);
    }

    document.cookie = `authToken=${JSON.stringify(tokens)};domain=${COOKIE_DOMAIN};path=/`;

    return of(tokens);
  }

  public setAccessToken(token: string): Observable<ITokens> {
    return this.tokens$.pipe(
      RxOp.first(),
      RxOp.switchMap(({ jwt_refresh_token }) => {
        return storageService.set<ITokens>('authToken', { jwt_token: token, jwt_refresh_token });
      }),
      RxOp.tap(tokens => this.tokens$.next(tokens)),
    );
  }

  public setTokenAcpo(token: string): Observable<string> {
    return this.storageService.set('authTokenAcpo', token).pipe(
      RxOp.tap(() =>
        this.getTokens().pipe(
          RxOp.tap(tokens =>
            this.tokens$.next({
              jwt_token: token,
              jwt_refresh_token: tokens.jwt_refresh_token,
            }),
          ),
        ),
      ),
    );
  }

  public getAcpoToken(): Observable<any> {
    return this.storageService.get('authTokenAcpo');
  }

  public clearToken() {
    this.setTokens(null);
  }

  public decode<T>(token: string): T {
    try {
      return jwtDecode<T>(token);
    } catch (_err) {
      console.error('failed to decode token!');
      return null;
    }
  }
}

const tokenService = new TokenService(storageService);
export default tokenService;
